import "isomorphic-fetch";
import { ITeamChannelDocumentLibrary } from "../../src/taskpane/interfaces/services/ITeamChannelDocumentLibrary";
import { logger } from "../../utilities/logger";
import { getAuthenticatedClient } from "../../src/taskpane/services/GraphClientService";
import { SharePointSiteHelper } from "../../utilities/SharePointSiteHelper";
import { ISiteInfo, filterNonTopLevelSites } from "../../src/taskpane/interfaces/ISiteInfo";
import { getItemCustomSelect, getTeamPrimaryChannel } from "../../src/taskpane/services/GraphService";
import { getTessAccountProjects, ITessDossier, ITessProject, ITessProjectFolders, TessProjectStatusEnum, TessProjectStatuses } from "../../src/taskpane/services/TessService";
import { tryDecodeUriComponent } from "../../utilities/EncodeDecode";
import { SharePointFolderContentTypeIds } from "../../src/taskpane/config/constants";

export enum LocationType {
    HubSite,
    SubSite,
    TeamChannel,
    DocumentLibrary,
    SharepointList,
    Folder,
    TessProject
}

export interface ILocation {
    key: string;
    id: string;
    displayName: string;
    description?: string;
    createdDateTime: string;
    createdDateTimeValue: number;
    lastModifiedDateTime: string;
    lastModifiedDateTimeValue: number;
    webUrl: string;
    type: LocationType;
    isPinned?: boolean;
    isHub?: boolean;
    hubId?: string;
    parentHubId?: string;
    tessProjectId?: string;
}

export interface ITeamChannelLocation extends ILocation {
    email: string;
    isPrivate: boolean;
    isShared: boolean;
    isPrimary: boolean;
    driveInfo?: ITeamChannelDocumentLibrary;
}

export interface ISharepointListLocation extends ILocation {
    rootItemCount?: number;
    itemCount?: number;
    sharePointListId: string;
    contentTypeId?: string;
    siteUrl?: string;
    isCalculatingRootItemCount?: boolean;
}

export interface ISharepointFolderLocation extends ISharepointListLocation {
    driveId: string;
    path: string;
    siteId: string;
    siteUrl: string;
    sharePointUniqueId: string;
}

export interface ITessProjectLocation extends ITessProject, ILocation {
    status: TessProjectStatusEnum;
}

export interface ITessProjectContent {
    libraries: ISharepointListLocation[];
    folders: ISharepointFolderLocation[];
    projects?: ITessProjectLocation[];
}

export const getLocationSubSites = async (accessToken: string, siteId: string, allowedSites: string[], isHubSitesRequest: boolean = false): Promise<ILocation[]> => {
    try {
        const client = getAuthenticatedClient(accessToken);

        const requestPath: string = isHubSitesRequest
            ? `/sites?search=(contentclass:STS_Site OR contentclass:STS_Web) AND (DepartmentId:{${siteId}} OR DepartmentId:${siteId} OR (IsHubSite:true AND RelatedHubSites:${siteId})) -SiteId:${siteId}`
            : `/sites/${siteId}/sites`;

        let result = await client.api(requestPath).select("id,displayName,description,createdDateTime,lastModifiedDateTime,webUrl,root").get();
        let subSites: any[] = result?.value?.filter(site => site.displayName?.length > 0) ?? [];

        let associatedHubSites: ISiteInfo[];
        let associatedHubSitesAvailable: boolean;
        if (siteId && isHubSitesRequest && SharePointSiteHelper.hubSites?.length > 0) {
            associatedHubSites = SharePointSiteHelper.hubSites.filter(hub => hub.parentHubId && hub.parentHubId.toLowerCase() === siteId.toLowerCase());
            associatedHubSitesAvailable = Array.isArray(associatedHubSites) && associatedHubSites.length > 0;
        }

        if (subSites?.length > 0 || associatedHubSitesAvailable) {
            let nextLink: string | null;
            if (result) nextLink = result["@odata.nextLink"] ?? null;
            if (nextLink?.length > 0) {
                try {
                    let subSiteCount: number = subSites.length;
                    let pageCount: number = 0;
                    while (nextLink && pageCount++ < 10) {
                        result = await client.api(nextLink).get();
                        const extraSites: any[] = result?.value?.filter(site => site.displayName?.length > 0) ?? [];
                        const extraSiteCount: number = extraSites.length;
                        for (let i = 0; i < extraSiteCount; i++) {
                            subSites[subSiteCount++] = extraSites[i];
                        }
                        nextLink = result ? result["@odata.nextLink"] : null;
                    }
                } catch (error) {
                    logger(error, "LocationService.ts (getLocationSubSites: paging loop)");
                }
            }

            if (allowedSites?.length > 0 && subSites) {
                subSites = subSites.filter(subSite => allowedSites.some(allowedSite => subSite.displayName?.trim().toLowerCase() === allowedSite.trim()));
            }

            if (!Array.isArray(subSites)) subSites = [];

            if (isHubSitesRequest && SharePointSiteHelper.hubSites?.length > 0) {
                if (subSites.length > 0) {
                    subSites.forEach(subSite => {
                        const hub: ISiteInfo = SharePointSiteHelper.hubSites.find(hubSite => hubSite.id === subSite.id);
                        if (hub) {
                            subSite.isHub = true;
                            subSite.hubId = hub.hubId;
                            if (hub.name) subSite.displayName = hub.name;
                            if (hub.description) subSite.description = hub.description;
                            if (hub.parentHubId) subSite.parentHubId = hub.parentHubId;
                        }
                    });
                }

                if (associatedHubSitesAvailable) {
                    associatedHubSites.forEach(associatedHub => {
                        const hubExists: boolean = subSites.some(subSite => subSite.id?.trim().toLowerCase() === associatedHub.id?.trim().toLowerCase());
                        if (!hubExists) {
                            subSites.push({
                                ...associatedHub,
                                displayName: associatedHub.name
                            });
                        }
                    });
                }
            }

            return subSites
                .filter(subSite => !subSite.parentHubId || subSite.parentHubId === siteId)
                .filter(filterNonTopLevelSites)
                .map((subSite, index) => {
                    const createdDate = new Date(subSite.createdDateTime);
                    const modifiedDate = new Date(subSite.lastModifiedDateTime);
                    return <ILocation>{
                        key: `s${index}`,
                        id: subSite.id,
                        displayName: subSite.displayName,
                        description: subSite.description,
                        createdDateTime: createdDate ? createdDate.toLocaleDateString(navigator.languages) : "-",
                        createdDateTimeValue: createdDate ? createdDate.valueOf() : 0,
                        lastModifiedDateTime: modifiedDate ? modifiedDate.toLocaleDateString(navigator.languages) : "-",
                        lastModifiedDateTimeValue: modifiedDate ? modifiedDate.valueOf() : 0,
                        webUrl: subSite.webUrl,
                        type: subSite.isHub ? LocationType.HubSite : LocationType.SubSite,
                        isHub: subSite.isHub,
                        hubId: subSite.hubId,
                        parentHubId: subSite.parentHubId
                    }
                })
                .sort((a, b) => a.displayName?.toUpperCase() < b.displayName?.toUpperCase() ? -1 : 1);
        }
    } catch (error) {
        logger(error, "LocationService.ts (getLocationSubSites)");
    }
    return [];
};

export const getLocationTeamChannels = async (accessToken: string, groupId: string): Promise<ITeamChannelLocation[]> => {
    if (groupId) {
        try {
            const client = getAuthenticatedClient(accessToken);
            // const teamChannels = (await client.api(`/teams/${groupId}/channels`).header("Prefer", "include-unknown-enum-members").get())?.value;
            const [teamChannels, primaryChannel] = await Promise.all([
                client.api(`/teams/${groupId}/channels`).header("Prefer", "include-unknown-enum-members").get(),
                getTeamPrimaryChannel(accessToken, groupId)
            ]);

            if (Array.isArray(teamChannels?.value)) {
                const channels: ITeamChannelLocation[] = teamChannels.value.map((teamChannel, index) => {
                    const createdDate = new Date(teamChannel.createdDateTime);
                    return <ITeamChannelLocation>{
                        key: `c${index}`,
                        id: teamChannel.id,
                        displayName: teamChannel.displayName,
                        description: teamChannel.description,
                        createdDateTime: createdDate.toLocaleDateString(navigator.languages),
                        createdDateTimeValue: createdDate.valueOf(),
                        lastModifiedDateTime: createdDate.toLocaleDateString(navigator.languages),
                        lastModifiedDateTimeValue: createdDate.valueOf(),
                        webUrl: teamChannel.webUrl,
                        email: teamChannel.email,
                        isPrivate: teamChannel.membershipType === "private",
                        isShared: teamChannel.membershipType === "shared",
                        isPrimary: teamChannel.id === primaryChannel?.id,
                        type: LocationType.TeamChannel
                    }
                });

                return [
                    ...channels.filter(x => x.isPrimary),
                    ...channels
                        .filter(x => !x.isPrimary)
                        .sort((channelA, channelB) => channelA.displayName?.toUpperCase() < channelB.displayName?.toUpperCase() ? -1 : 1)
                ];
            }
        } catch (error) {
            logger(error, "LocationService.ts (getLocationTeamChannels)");
        }
    }
    return [];
};

export const getLocationDocumentLibraries = async (accessToken: string, siteId: string): Promise<ISharepointListLocation[]> => {
    try {
        const client = getAuthenticatedClient(accessToken);
        const documentLibraries = (
            await client.api(`/sites/${siteId}/drives`)
                .select("id,name,description,createdDateTime,lastModifiedDateTime,webUrl,sharepointIds")
                .expand("list($select=displayName)")
                .get()
        )?.value;

        if (documentLibraries) {
            return documentLibraries
                .map((docLib, index) => {
                    const createdDate = new Date(docLib.createdDateTime);
                    const modifiedDate = new Date(docLib.lastModifiedDateTime);
                    return <ISharepointListLocation>{
                        key: `d${index}`,
                        id: docLib.id,
                        displayName: docLib.list?.displayName ?? docLib.name,
                        description: docLib.description,
                        createdDateTime: createdDate.toLocaleDateString(navigator.languages),
                        createdDateTimeValue: createdDate.valueOf(),
                        lastModifiedDateTime: modifiedDate.toLocaleDateString(navigator.languages),
                        lastModifiedDateTimeValue: modifiedDate.valueOf(),
                        webUrl: docLib.webUrl,
                        type: LocationType.DocumentLibrary,
                        sharePointListId: docLib.sharePointIds.listId
                    }
                })
                .sort((a, b) => a.displayName?.toUpperCase() < b.displayName?.toUpperCase() ? -1 : 1);
        }
    } catch (error) {
        logger(error, "LocationService.ts (getLocationDocumentLibraries)");
    }
    return [];
};

export const getLocationSharepointLists = async (accessToken: string, siteId: string): Promise<ISharepointListLocation[]> => {
    try {
        const client = getAuthenticatedClient(accessToken);
        const sharepointLists = (await client.api(`/sites/${siteId}/lists`).select("id,name,displayName,description,createdDateTime,lastModifiedDateTime,webUrl,sharepointIds,list").get())?.value;

        if (sharepointLists) {
            return sharepointLists.filter(x => (x.list.template === "genericList" || x.list.template === "customGrid") && !x.list.hidden).map((spListItem, index) => {
                const createdDate = new Date(spListItem.createdDateTime);
                const modifiedDate = new Date(spListItem.lastModifiedDateTime);
                return <ISharepointListLocation>{
                    key: `l${index}`,
                    id: spListItem.id,
                    displayName: spListItem.displayName ?? spListItem.name,
                    description: spListItem.description,
                    createdDateTime: createdDate.toLocaleDateString(navigator.languages),
                    createdDateTimeValue: createdDate.valueOf(),
                    lastModifiedDateTime: modifiedDate.toLocaleDateString(navigator.languages),
                    lastModifiedDateTimeValue: modifiedDate.valueOf(),
                    webUrl: spListItem.webUrl,
                    type: LocationType.SharepointList,
                    sharePointListId: spListItem.sharepointIds.listId
                }
            })
                .sort((a, b) => a.displayName?.toUpperCase() < b.displayName?.toUpperCase() ? -1 : 1);
        }
    } catch (error) {
        logger(error, "LocationService.ts (getLocationSharpointLists)");
    }
    return [];
};

export const getLocationTessProjects = async (accessToken: string, tessAccountId: string): Promise<ITessProjectContent> => {
    try {
        let projectContent: ITessProjectContent = {
            libraries: [],
            folders: [],
            projects: []
        }

        const projectFolders: ITessProjectFolders = await getTessAccountProjects(tessAccountId);
        if (projectFolders) {
            if (projectFolders.account?.dossierMetadata?.length > 0) {
                projectContent = await getTessDossierData(accessToken, projectFolders.account.dossierMetadata);
            }
            
            const allProjects: ITessProjectLocation[] = [];
            await Promise.all(TessProjectStatuses.map(async (status) => {
                const projectSites = (await Promise.all(projectFolders[status.key].map(async (project, index) => {
                    if (Array.isArray(project.dossierMetadata) && project.dossierMetadata.length > 0) {
                        // const siteHit: IHit = await getSharePointSiteFromDriveId(project.dossierMetadata[0].driveId);
                        // if (siteHit) {
                        //     const site = siteHit.resource;
                        //     const createdDate = new Date(site.createdDateTime);
                        //     const modifiedDate = new Date(site.lastModifiedDateTime);
                        //     return <ITessProjectLocation>{
                        //         key: `ps_${status.key.toString()}_${index}`,
                        //         id: site.id,
                        //         displayName: project.name,
                        //         description: site.description,
                        //         createdDateTime: createdDate ? createdDate.toLocaleDateString() : "-",
                        //         createdDateTimeValue: createdDate ? createdDate.valueOf() : 0,
                        //         lastModifiedDateTime: modifiedDate ? modifiedDate.toLocaleDateString() : "-",
                        //         lastModifiedDateTimeValue: modifiedDate ? modifiedDate.valueOf() : 0,
                        //         webUrl: site.webUrl,
                        //         type: LocationType.SubSite,
                        //         status: status.key,
                        //         tessProjectId: project.id
                        //     }
                        // }
                        return <ITessProjectLocation>{
                            ...project,
                            key: `ps_${status.key.toString()}_${index}`,
                            id: project.id,
                            displayName: project.name,
                            type: LocationType.TessProject,
                            status: status.key,
                            tessProjectId: project.id
                        }
                    }
                    return null;
                })))
                    .filter(x => x);

                allProjects.push(...projectSites);
            }));

            projectContent.projects = allProjects.sort((a, b) => a.displayName?.toUpperCase() < b.displayName?.toUpperCase() ? -1 : 1);
        }

        return projectContent;
    } catch (error) {
        logger(error, "LocationService.ts (getLocationTessProjects)");
    }

    return null;
};

export const getLocationTessProjectLibrariesAndFolders = async (accessToken: string, tessAccountId: string, tessProjectId: string): Promise<ITessProjectContent> => {
    try {
        const projects: ITessProjectFolders = await getTessAccountProjects(tessAccountId);
        if (projects) {
            const project: ITessProject =
                projects.active.find(x => x.id === tessProjectId)
                || projects.drafts.find(x => x.id === tessProjectId)
                || projects.finished.find(x => x.id === tessProjectId);

            if (project?.dossierMetadata?.length > 0) {
                return await getTessDossierData(accessToken, project.dossierMetadata);
            }
        }
    } catch (error) {
        logger(error, "LocationService.ts (getLocationTessProjects)");
    }

    return { libraries: [], folders: [] };
}

const getTessDossierData = async (accessToken: string, dossierMetadata: ITessDossier[]): Promise<ITessProjectContent> => {
    const documentLibraries: ISharepointListLocation[] = [];
    const projectFolders: ISharepointFolderLocation[] = [];

    await Promise.all(dossierMetadata
        .filter(dossier => dossier.driveId && !dossier.itemId)
        .map(async (dossier, index) => {
            const documentLibraryData = await getItemCustomSelect(accessToken, dossier.driveId, null, "id,name,description,createdDateTime,lastModifiedDateTime,webUrl,sharepointIds");
            if (documentLibraryData) {
                const createdDate = new Date(documentLibraryData.createdDateTime);
                const modifiedDate = new Date(documentLibraryData.lastModifiedDateTime);
                documentLibraries.push({
                    key: `lib${index}`,
                    id: documentLibraryData.id,
                    displayName: dossier.name,
                    description: documentLibraryData.description,
                    createdDateTime: createdDate.toLocaleDateString(navigator.languages),
                    createdDateTimeValue: createdDate.valueOf(),
                    lastModifiedDateTime: modifiedDate.toLocaleDateString(navigator.languages),
                    lastModifiedDateTimeValue: modifiedDate.valueOf(),
                    webUrl: documentLibraryData.webUrl,
                    type: LocationType.DocumentLibrary,
                    sharePointListId: documentLibraryData.sharePointIds.listId,
                    siteUrl: documentLibraryData.sharePointIds.siteUrl
                });
            }
        }));

    await Promise.all(dossierMetadata
        .filter(dossier => dossier.driveId && dossier.itemId)
        .map(async (dossier, index) => {
            const folderData = await getItemCustomSelect(accessToken, dossier.driveId, dossier.itemId, "id,name,description,createdDateTime,lastModifiedDateTime,webUrl,webDavUrl,sharepointIds");
            if (folderData) {
                const createdDate = new Date(folderData.createdDateTime);
                const modifiedDate = new Date(folderData.lastModifiedDateTime);
                projectFolders.push({
                    key: `fld${index}`,
                    id: folderData.id,
                    driveId: dossier.driveId,
                    path: tryDecodeUriComponent(new URL(folderData.webDavUrl).pathname),
                    displayName: dossier.name,
                    description: folderData.description,
                    createdDateTime: createdDate.toLocaleDateString(navigator.languages),
                    createdDateTimeValue: createdDate.valueOf(),
                    lastModifiedDateTime: modifiedDate.toLocaleDateString(navigator.languages),
                    lastModifiedDateTimeValue: modifiedDate.valueOf(),
                    webUrl: folderData.webUrl,
                    siteId: folderData.sharepointIds.siteId,
                    siteUrl: folderData.sharepointIds.siteUrl,
                    type: LocationType.Folder,
                    contentTypeId: SharePointFolderContentTypeIds.folder,
                    sharePointListId: folderData.sharepointIds.listId,
                    sharePointUniqueId: folderData.sharepointIds.listItemUniqueId,
                });
            }
        }));

    return { libraries: documentLibraries, folders: projectFolders };
}